import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BACKEND_URL } from '../config/canonical';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { CheckCircle2, AlertCircle, Upload as UploadIcon, X, Plus } from 'lucide-react';

// -------- UI helpers ---------------------------------------------------------
const Section = ({ title, subtitle, children }) => (
  <div className="border-b border-white/10 pb-8 mb-8">
    <h3 className="text-sm font-heading font-semibold text-amber-300 mb-1 uppercase tracking-widest">{title}</h3>
    {subtitle && <p className="text-xs text-slate-300 mb-4">{subtitle}</p>}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">{children}</div>
  </div>
);

const Field = ({ label, children, full, hint }) => (
  <div className={full ? 'md:col-span-2' : ''}>
    <Label className="text-xs text-slate-200 uppercase tracking-wider font-mono">{label}</Label>
    <div className="mt-1">{children}</div>
    {hint && <p className="text-[10px] text-slate-400 mt-1">{hint}</p>}
  </div>
);

// Dark-form-friendly input styling (high contrast)
const inputCls = 'bg-[#0F2340] border-white/20 !text-white placeholder:text-slate-400 focus-visible:ring-amber-400';

const YesNo = ({ value, onChange, name }) => (
  <RadioGroup value={value === null || value === undefined ? '' : (value ? 'yes' : 'no')} onValueChange={v => onChange(v === 'yes')} className="flex gap-6 mt-1" data-testid={`${name}-radio`}>
    <div className="flex items-center gap-2"><RadioGroupItem value="yes" id={`${name}-yes`} /><Label htmlFor={`${name}-yes`} className="text-sm text-slate-100">Yes</Label></div>
    <div className="flex items-center gap-2"><RadioGroupItem value="no" id={`${name}-no`} /><Label htmlFor={`${name}-no`} className="text-sm text-slate-100">No</Label></div>
  </RadioGroup>
);

const MultiCheck = ({ value = [], options, onChange, name }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
    {options.map(opt => {
      const checked = value.includes(opt);
      return (
        <label key={opt} className="flex items-center gap-2 text-sm text-slate-100" data-testid={`${name}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
          <Checkbox checked={checked} onCheckedChange={() => {
            onChange(checked ? value.filter(v => v !== opt) : [...value, opt]);
          }} />
          {opt}
        </label>
      );
    })}
  </div>
);

// -------- Component ----------------------------------------------------------
const Apply = () => {
  const [f, setF] = useState({
    // Personal
    first_name: '', last_name: '', full_legal_name: '', email: '', phone: '',
    date_of_birth: '', ssn_last4: '',
    drivers_license_number: '', drivers_license_state: '',
    address_line1: '', address_line2: '', city: '', state: '', postal_code: '',
    emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
    // Position
    position_applied: 'Field Investigator', employment_type: '', desired_pay_rate: '', earliest_start_date: '',
    // Texas Licensing
    holds_tx_pi_license: '', license_type: '', pi_license_number: '', pi_license_state: 'TX',
    license_expiration: '',
    eligible_to_register_tx_dps: null,
    license_revoked_or_suspended: null, license_revocation_explanation: '',
    // Investigative Experience
    years_investigative_experience: '', investigation_types: [], experience_description: '',
    // Specialized Skills
    specialized_skills: [], additional_skills: '',
    // Technology
    tech_case_mgmt: null, tech_surveillance_cameras: null, tech_gps_devices: null,
    tech_evidence_storage: null, tech_report_software: null, investigative_software_used: '',
    // Vehicle
    has_reliable_vehicle: null, vehicle_make_model: '', vehicle_year: '', vehicle_license_plate: '',
    equipment_owned: [],
    // Criminal History
    felony_conviction: null, misdemeanor_ab_conviction: null, currently_under_investigation: null,
    criminal_history_details: '',
    // Education
    highest_education: '', school_name: '', area_of_study: '',
    // Prior Employment
    prior_experience_years: '', prior_agency: '', prior_role: '',
    prior_employment_start: '', prior_employment_end: '',
    prior_supervisor_name: '', prior_supervisor_phone: '', prior_reason_for_leaving: '',
    relevant_certifications: '',
    // Military / LE
    has_military_background: false, military_branch: '', military_rank: '', military_service_years: '',
    has_law_enforcement_experience: false, le_agency: '', le_role: '', le_service_years: '',
    // References
    reference1_name: '', reference1_relationship: '', reference1_phone: '',
    reference2_name: '', reference2_relationship: '', reference2_phone: '',
    reference3_name: '', reference3_relationship: '', reference3_phone: '',
    // Consents
    consent_background_check: false, consent_driving_record: false,
    consent_reference_verification: false, consent_tx_dps_verification: false,
    consent_initials_background: '',
    consent_confidentiality: false, consent_initials_confidentiality: '',
    consent_drug_test: false,
    // Certification
    certified_true: false, applicant_signature: '', signature_date: new Date().toISOString().slice(0, 10),
    // Freeform
    why_interested: '', availability_notes: '',
    // Iter121A extended fields
    photo_url: '', license_front_url: '', license_back_url: '',
    authorized_to_work_us: null, over_18: null,
    // Prior employer entries beyond the "most recent" block above.
    // Each: {employer, role, start_date, end_date, supervisor_name, supervisor_phone,
    //        reason_for_leaving, may_contact}
    work_history: [],
    education_history: [], // {institution, degree, field_of_study, start_date, end_date, gpa}
    certifications_list: [], // {name, issuer, license_number, issued_date, expiration_date}
    availability_days: [], availability_shifts: [],
    willing_to_travel: null, willing_to_relocate: null, max_weekly_hours: '',
    ever_terminated_or_asked_to_resign: null, termination_details: '',
    financial_judgments_or_bankruptcy: null, financial_details: '',
    substance_abuse_treatment: null, substance_abuse_details: '',
    can_pass_drug_test: null,
    languages_spoken: [], languages_written: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploadingKind, setUploadingKind] = useState(null);
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');

  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  // ---- upload helpers -----------------------------------------------------
  const uploadAttachment = async (kind, file) => {
    if (!file) return;
    setUploadingKind(kind);
    try {
      const fd = new FormData();
      fd.append('kind', kind);
      fd.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/applicants/upload-attachment`, {
        method: 'POST', body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Upload failed');
      const map = {
        headshot: 'photo_url',
        license_front: 'license_front_url',
        license_back: 'license_back_url',
      };
      set(map[kind], data.file_url);
    } catch (err) {
      setError(`${kind} upload failed: ${err.message}`);
    } finally {
      setUploadingKind(null);
    }
  };

  // ---- array-field helpers ------------------------------------------------
  const addWorkEntry = () => set('work_history', [
    ...f.work_history,
    { employer: '', role: '', start_date: '', end_date: '',
      supervisor_name: '', supervisor_phone: '', reason_for_leaving: '', may_contact: true },
  ]);
  const updateWorkEntry = (idx, k, v) => set('work_history',
    f.work_history.map((row, i) => (i === idx ? { ...row, [k]: v } : row)));
  const removeWorkEntry = (idx) => set('work_history',
    f.work_history.filter((_, i) => i !== idx));

  const addEduEntry = () => set('education_history', [
    ...f.education_history,
    { institution: '', degree: '', field_of_study: '', start_date: '', end_date: '', gpa: '' },
  ]);
  const updateEduEntry = (idx, k, v) => set('education_history',
    f.education_history.map((row, i) => (i === idx ? { ...row, [k]: v } : row)));
  const removeEduEntry = (idx) => set('education_history',
    f.education_history.filter((_, i) => i !== idx));

  const addCertEntry = () => set('certifications_list', [
    ...f.certifications_list,
    { name: '', issuer: '', license_number: '', issued_date: '', expiration_date: '' },
  ]);
  const updateCertEntry = (idx, k, v) => set('certifications_list',
    f.certifications_list.map((row, i) => (i === idx ? { ...row, [k]: v } : row)));
  const removeCertEntry = (idx) => set('certifications_list',
    f.certifications_list.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!f.first_name || !f.last_name || !f.email || !f.phone) {
      setError('First name, last name, email and phone are required.');
      return;
    }
    if (!f.certified_true || !f.applicant_signature) {
      setError('You must certify the information and provide your typed signature.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...f,
        prior_experience_years: f.prior_experience_years ? Number(f.prior_experience_years) : null,
      };
      const res = await fetch(`${BACKEND_URL}/api/applicants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Submission failed');
      setDone(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#0B192C] flex items-center justify-center px-4">
        <div className="max-w-lg w-full bg-[#0F2340] border border-emerald-500/40 rounded-sm p-8 text-center" data-testid="apply-success">
          <img src="/nexus-patch.png" alt="Nexus" className="w-28 h-28 mx-auto mb-3 object-contain" />
          <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
          <h1 className="text-2xl font-heading font-bold text-white mb-2">Application received</h1>
          <p className="text-sm text-slate-200 mb-1">Reference: <span className="font-mono text-amber-300" data-testid="apply-reference">{done.id}</span></p>
          {done.portal_username && (
            <div className="mt-4 mb-5 bg-[#0B192C] border border-amber-400/60 rounded p-4">
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-300">Your Nexus portal username</p>
              <p className="text-2xl font-heading font-bold text-amber-300 mt-1" data-testid="apply-portal-username">{done.portal_username}</p>
              <p className="text-xs text-slate-300 mt-2">
                Check your email for your temporary password, then sign in to the NexusTrac
                applicant portal.
              </p>
            </div>
          )}
          <p className="text-sm text-slate-300 mb-6">
            A Nexus board member will review your application and contact you using the email / phone you provided. Next step will be the IPS-OIE psychological suitability exam if you move forward.
          </p>
          <Link to="/login"><Button className="bg-amber-400 text-[#0B192C] hover:bg-amber-300 font-heading font-semibold rounded-sm" data-testid="apply-back-to-login">Sign in to NexusTrac</Button></Link>
        </div>
      </div>
    );
  }

  const invTypes = ['Surveillance', 'Insurance Fraud', 'Workers Comp', 'Background Checks', 'Domestic / Infidelity', 'Skip Tracing', 'Process Serving', 'Missing Persons', 'Corporate / Intellectual Property', 'Civil Litigation Support', 'Criminal Defense Support'];
  const skillsList = ['Firearms Certified', 'Defensive Tactics', 'First Aid / CPR', 'Bilingual', 'Report Writing', 'Interview & Interrogation', 'Video / Photo Evidence', 'Social Media / OSINT', 'Court Testimony'];
  const equipmentList = ['Covert Camera(s)', 'GPS Tracker', 'Body Cam', 'Binoculars', 'Dash Cam', 'Night Vision', 'Audio Recorder', 'Laptop / Mobile Office'];

  return (
    <div className="min-h-screen bg-[#0B192C] py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-start mb-4">
          <Link to="/login">
            <Button type="button" variant="outline"
              className="h-9 border-amber-500/40 !text-amber-200 hover:!bg-amber-500/10 font-mono uppercase tracking-wider text-[11px]"
              data-testid="apply-back-to-login-header">
              ← Back to Login
            </Button>
          </Link>
        </div>
        <div className="text-center mb-8">
          <img src="/nexus-patch.png" alt="Nexus" className="w-32 h-32 mx-auto mb-3 object-contain" data-testid="apply-logo" />
          <h1 className="text-3xl font-heading font-bold text-white">Apply to Nexus Investigation Agency</h1>
          <p className="text-xs text-slate-300 font-mono uppercase tracking-[0.25em] mt-1">Employment Application</p>
          <p className="text-[11px] text-slate-400 mt-2">All information is confidential and used for hiring evaluation only.</p>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 mb-6 bg-red-500/15 border border-red-500/40 rounded-sm text-red-200 text-sm" data-testid="apply-error">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <form onSubmit={submit} className="bg-[#0F2340] border border-white/10 rounded-sm p-6 shadow-lg">
          {/* 0. Photo & ID Uploads (Iter121A) */}
          <Section title="0. Photo & ID Uploads" subtitle="Company ID card + license verification. JPG/PNG/PDF up to 10MB each.">
            {/* Headshot */}
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-200 uppercase tracking-wider font-mono">Headshot Photo (for Company ID card)</Label>
              <div className="mt-2 flex items-center gap-4">
                <div className="w-24 h-24 rounded-lg border-2 border-dashed border-slate-500 bg-[#0B192C] flex items-center justify-center overflow-hidden">
                  {f.photo_url ? (
                    <img src={f.photo_url} alt="Headshot preview" className="w-full h-full object-cover" data-testid="apply-headshot-preview" />
                  ) : (
                    <UploadIcon size={22} className="text-slate-500" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="cursor-pointer inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider bg-amber-400/90 text-[#0B192C] px-3 py-1.5 rounded-sm hover:bg-amber-300">
                    <UploadIcon size={12} /> {uploadingKind === 'headshot' ? 'Uploading…' : (f.photo_url ? 'Replace Photo' : 'Upload Photo')}
                    <input
                      type="file" accept="image/*" className="hidden"
                      onChange={(e) => uploadAttachment('headshot', e.target.files?.[0])}
                      data-testid="apply-headshot-input"
                    />
                  </label>
                  {f.photo_url && (
                    <button type="button" onClick={() => set('photo_url', '')}
                      className="text-[10px] text-slate-400 hover:text-white font-mono uppercase tracking-wider text-left flex items-center gap-1"
                      data-testid="apply-headshot-remove"
                    >
                      <X size={10} /> Remove
                    </button>
                  )}
                  <p className="text-[10px] text-slate-400">Chest-up, plain background, professional attire.</p>
                </div>
              </div>
            </div>

            {/* License Front */}
            <div>
              <Label className="text-xs text-slate-200 uppercase tracking-wider font-mono">License / ID — Front</Label>
              <div className="mt-2 flex items-center gap-3">
                <div className="w-28 h-16 rounded-md border-2 border-dashed border-slate-500 bg-[#0B192C] flex items-center justify-center overflow-hidden">
                  {f.license_front_url ? (
                    <img src={f.license_front_url} alt="License front" className="w-full h-full object-cover" data-testid="apply-license-front-preview" />
                  ) : (
                    <UploadIcon size={18} className="text-slate-500" />
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <label className="cursor-pointer text-[10px] font-mono uppercase tracking-wider bg-slate-700 text-white px-2.5 py-1.5 rounded-sm hover:bg-slate-600 inline-flex items-center gap-1">
                    <UploadIcon size={10} /> {uploadingKind === 'license_front' ? 'Uploading…' : (f.license_front_url ? 'Replace' : 'Upload')}
                    <input
                      type="file" accept="image/*,application/pdf" className="hidden"
                      onChange={(e) => uploadAttachment('license_front', e.target.files?.[0])}
                      data-testid="apply-license-front-input"
                    />
                  </label>
                  {f.license_front_url && (
                    <button type="button" onClick={() => set('license_front_url', '')}
                      className="text-[10px] text-slate-400 hover:text-white font-mono uppercase tracking-wider text-left">
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* License Back */}
            <div>
              <Label className="text-xs text-slate-200 uppercase tracking-wider font-mono">License / ID — Back</Label>
              <div className="mt-2 flex items-center gap-3">
                <div className="w-28 h-16 rounded-md border-2 border-dashed border-slate-500 bg-[#0B192C] flex items-center justify-center overflow-hidden">
                  {f.license_back_url ? (
                    <img src={f.license_back_url} alt="License back" className="w-full h-full object-cover" data-testid="apply-license-back-preview" />
                  ) : (
                    <UploadIcon size={18} className="text-slate-500" />
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <label className="cursor-pointer text-[10px] font-mono uppercase tracking-wider bg-slate-700 text-white px-2.5 py-1.5 rounded-sm hover:bg-slate-600 inline-flex items-center gap-1">
                    <UploadIcon size={10} /> {uploadingKind === 'license_back' ? 'Uploading…' : (f.license_back_url ? 'Replace' : 'Upload')}
                    <input
                      type="file" accept="image/*,application/pdf" className="hidden"
                      onChange={(e) => uploadAttachment('license_back', e.target.files?.[0])}
                      data-testid="apply-license-back-input"
                    />
                  </label>
                  {f.license_back_url && (
                    <button type="button" onClick={() => set('license_back_url', '')}
                      className="text-[10px] text-slate-400 hover:text-white font-mono uppercase tracking-wider text-left">
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Eligibility */}
            <Field label="Are you 18 or older?"><YesNo name="over-18" value={f.over_18} onChange={v => set('over_18', v)} /></Field>
            <Field label="Authorized to work in the United States?"><YesNo name="us-work" value={f.authorized_to_work_us} onChange={v => set('authorized_to_work_us', v)} /></Field>
          </Section>

          {/* 1. Applicant Information */}
          <Section title="1. Applicant Information">
            <Field label="First Name *"><Input data-testid="apply-first-name" value={f.first_name} onChange={e => set('first_name', e.target.value)} className={inputCls} required /></Field>
            <Field label="Last Name *"><Input data-testid="apply-last-name" value={f.last_name} onChange={e => set('last_name', e.target.value)} className={inputCls} required /></Field>
            <Field full label="Full Legal Name (as it appears on your ID)"><Input value={f.full_legal_name} onChange={e => set('full_legal_name', e.target.value)} className={inputCls} /></Field>
            <Field label="Email *"><Input data-testid="apply-email" type="email" value={f.email} onChange={e => set('email', e.target.value)} className={inputCls} required /></Field>
            <Field label="Phone *"><Input data-testid="apply-phone" value={f.phone} onChange={e => set('phone', e.target.value)} className={inputCls} required /></Field>
            <Field label="Date of Birth"><Input type="date" value={f.date_of_birth} onChange={e => set('date_of_birth', e.target.value)} className={inputCls} /></Field>
            <Field label="SSN (last 4)" hint="Last 4 digits only. Full SSN collected later on a secure I-9 form."><Input maxLength={4} value={f.ssn_last4} onChange={e => set('ssn_last4', e.target.value.replace(/\D/g, '').slice(0, 4))} className={inputCls} placeholder="1234" /></Field>
            <Field label="Driver's License #"><Input value={f.drivers_license_number} onChange={e => set('drivers_license_number', e.target.value)} className={inputCls} /></Field>
            <Field label="Driver's License State"><Input value={f.drivers_license_state} onChange={e => set('drivers_license_state', e.target.value)} className={inputCls} placeholder="TX" /></Field>
            <Field full label="Address"><Input value={f.address_line1} onChange={e => set('address_line1', e.target.value)} className={inputCls} /></Field>
            <Field full label="Address Line 2"><Input value={f.address_line2} onChange={e => set('address_line2', e.target.value)} className={inputCls} placeholder="Apt, Suite, etc." /></Field>
            <Field label="City"><Input value={f.city} onChange={e => set('city', e.target.value)} className={inputCls} /></Field>
            <Field label="State"><Input value={f.state} onChange={e => set('state', e.target.value)} className={inputCls} /></Field>
            <Field label="ZIP"><Input value={f.postal_code} onChange={e => set('postal_code', e.target.value)} className={inputCls} /></Field>
            <Field label="Emergency Contact Name"><Input value={f.emergency_contact_name} onChange={e => set('emergency_contact_name', e.target.value)} className={inputCls} /></Field>
            <Field label="Emergency Contact Phone"><Input value={f.emergency_contact_phone} onChange={e => set('emergency_contact_phone', e.target.value)} className={inputCls} /></Field>
            <Field full label="Emergency Contact Relationship"><Input value={f.emergency_contact_relationship} onChange={e => set('emergency_contact_relationship', e.target.value)} className={inputCls} placeholder="Spouse / Parent / Sibling / etc." /></Field>
          </Section>

          {/* 2. Position */}
          <Section title="2. Position">
            <Field label="Position Applied For"><Input value={f.position_applied} onChange={e => set('position_applied', e.target.value)} className={inputCls} /></Field>
            <Field label="Employment Type">
              <RadioGroup value={f.employment_type} onValueChange={v => set('employment_type', v)} className="flex gap-4 mt-2">
                {['full_time', 'part_time', 'contract'].map(v => (
                  <div key={v} className="flex items-center gap-2"><RadioGroupItem value={v} id={`et-${v}`} /><Label htmlFor={`et-${v}`} className="text-sm text-slate-100 capitalize">{v.replace('_', '-')}</Label></div>
                ))}
              </RadioGroup>
            </Field>
            <Field label="Desired Pay Rate"><Input value={f.desired_pay_rate} onChange={e => set('desired_pay_rate', e.target.value)} className={inputCls} placeholder="e.g. $30/hr or $65,000/yr" /></Field>
            <Field label="Earliest Start Date"><Input type="date" value={f.earliest_start_date} onChange={e => set('earliest_start_date', e.target.value)} className={inputCls} /></Field>
          </Section>

          {/* 3. Texas Licensing */}
          <Section title="3. Texas Licensing">
            <Field full label="Do you currently hold a Texas PI license?">
              <RadioGroup value={f.holds_tx_pi_license} onValueChange={v => set('holds_tx_pi_license', v)} className="flex gap-6 mt-2">
                {['yes', 'no', 'applying'].map(v => (
                  <div key={v} className="flex items-center gap-2"><RadioGroupItem value={v} id={`pi-${v}`} /><Label htmlFor={`pi-${v}`} className="text-sm text-slate-100 capitalize">{v}</Label></div>
                ))}
              </RadioGroup>
            </Field>
            <Field label="License Type / Class"><Input value={f.license_type} onChange={e => set('license_type', e.target.value)} className={inputCls} placeholder="e.g. Class A / Class C" /></Field>
            <Field label="License Number"><Input value={f.pi_license_number} onChange={e => set('pi_license_number', e.target.value)} className={inputCls} /></Field>
            <Field label="License State"><Input value={f.pi_license_state} onChange={e => set('pi_license_state', e.target.value)} className={inputCls} /></Field>
            <Field label="License Expiration"><Input type="date" value={f.license_expiration} onChange={e => set('license_expiration', e.target.value)} className={inputCls} /></Field>
            <Field full label="Are you eligible to register with TX DPS under our company?"><YesNo name="dps-eligible" value={f.eligible_to_register_tx_dps} onChange={v => set('eligible_to_register_tx_dps', v)} /></Field>
            <Field full label="Have any licenses ever been revoked or suspended?"><YesNo name="license-revoked" value={f.license_revoked_or_suspended} onChange={v => set('license_revoked_or_suspended', v)} /></Field>
            {f.license_revoked_or_suspended && (
              <Field full label="If yes, explain"><Textarea rows={2} value={f.license_revocation_explanation} onChange={e => set('license_revocation_explanation', e.target.value)} className={inputCls} /></Field>
            )}
          </Section>

          {/* 4. Investigative Experience */}
          <Section title="4. Investigative Experience">
            <Field full label="Years of Investigative Experience">
              <RadioGroup value={f.years_investigative_experience} onValueChange={v => set('years_investigative_experience', v)} className="flex flex-wrap gap-4 mt-2">
                {['none', '1-2', '3-5', '5+'].map(v => (
                  <div key={v} className="flex items-center gap-2"><RadioGroupItem value={v} id={`yr-${v}`} /><Label htmlFor={`yr-${v}`} className="text-sm text-slate-100">{v}</Label></div>
                ))}
              </RadioGroup>
            </Field>
            <Field full label="Types of investigations performed (check all that apply)"><MultiCheck name="inv-types" options={invTypes} value={f.investigation_types} onChange={v => set('investigation_types', v)} /></Field>
            <Field full label="Briefly describe your investigative background"><Textarea rows={4} value={f.experience_description} onChange={e => set('experience_description', e.target.value)} className={inputCls} /></Field>
          </Section>

          {/* 5. Specialized Skills */}
          <Section title="5. Specialized Skills">
            <Field full label="Certifications & Skills (check all that apply)"><MultiCheck name="skills" options={skillsList} value={f.specialized_skills} onChange={v => set('specialized_skills', v)} /></Field>
            <Field full label="Additional Skills / Certifications"><Textarea rows={2} value={f.additional_skills} onChange={e => set('additional_skills', e.target.value)} className={inputCls} /></Field>
          </Section>

          {/* 6. Technology */}
          <Section title="6. Technology Proficiency">
            <Field label="Case management systems"><YesNo name="tech-case" value={f.tech_case_mgmt} onChange={v => set('tech_case_mgmt', v)} /></Field>
            <Field label="Surveillance cameras / covert recording"><YesNo name="tech-cam" value={f.tech_surveillance_cameras} onChange={v => set('tech_surveillance_cameras', v)} /></Field>
            <Field label="GPS tracking devices"><YesNo name="tech-gps" value={f.tech_gps_devices} onChange={v => set('tech_gps_devices', v)} /></Field>
            <Field label="Digital evidence storage / chain of custody"><YesNo name="tech-evidence" value={f.tech_evidence_storage} onChange={v => set('tech_evidence_storage', v)} /></Field>
            <Field label="Report writing software"><YesNo name="tech-report" value={f.tech_report_software} onChange={v => set('tech_report_software', v)} /></Field>
            <Field full label="List any investigative software you have used"><Input value={f.investigative_software_used} onChange={e => set('investigative_software_used', e.target.value)} className={inputCls} placeholder="e.g. TLO, IRB, CLEAR, Thomson Reuters" /></Field>
          </Section>

          {/* 7. Vehicle & Equipment */}
          <Section title="7. Vehicle & Equipment">
            <Field full label="Do you have a reliable personal vehicle?"><YesNo name="vehicle" value={f.has_reliable_vehicle} onChange={v => set('has_reliable_vehicle', v)} /></Field>
            <Field label="Vehicle Make / Model"><Input value={f.vehicle_make_model} onChange={e => set('vehicle_make_model', e.target.value)} className={inputCls} /></Field>
            <Field label="Vehicle Year"><Input value={f.vehicle_year} onChange={e => set('vehicle_year', e.target.value)} className={inputCls} /></Field>
            <Field label="License Plate"><Input value={f.vehicle_license_plate} onChange={e => set('vehicle_license_plate', e.target.value)} className={inputCls} /></Field>
            <Field full label="Equipment you personally own"><MultiCheck name="equip" options={equipmentList} value={f.equipment_owned} onChange={v => set('equipment_owned', v)} /></Field>
          </Section>

          {/* 8. Criminal History */}
          <Section title="8. Criminal History" subtitle="A prior record is NOT an automatic disqualifier. Failure to disclose is.">
            <Field full label="Ever convicted of a felony?"><YesNo name="felony" value={f.felony_conviction} onChange={v => set('felony_conviction', v)} /></Field>
            <Field full label="Ever convicted of a Class A or B misdemeanor?"><YesNo name="misd" value={f.misdemeanor_ab_conviction} onChange={v => set('misdemeanor_ab_conviction', v)} /></Field>
            <Field full label="Currently under criminal investigation?"><YesNo name="under-inv" value={f.currently_under_investigation} onChange={v => set('currently_under_investigation', v)} /></Field>
            <Field full label="If yes to any above, please explain (dates, charges, disposition)"><Textarea rows={3} value={f.criminal_history_details} onChange={e => set('criminal_history_details', e.target.value)} className={inputCls} /></Field>
          </Section>

          {/* 8b. Extended Background Disclosures (Iter121A) */}
          <Section title="8b. Additional Background Disclosures" subtitle="Full disclosure protects your hiring decision. All answers are confidential.">
            <Field full label="Have you ever been terminated, or asked to resign, from a job?"><YesNo name="terminated" value={f.ever_terminated_or_asked_to_resign} onChange={v => set('ever_terminated_or_asked_to_resign', v)} /></Field>
            {f.ever_terminated_or_asked_to_resign && (
              <Field full label="Please describe the circumstances"><Textarea rows={2} value={f.termination_details} onChange={e => set('termination_details', e.target.value)} className={inputCls} /></Field>
            )}
            <Field full label="Any financial judgments, garnishments, or bankruptcy in the last 7 years?"><YesNo name="financial" value={f.financial_judgments_or_bankruptcy} onChange={v => set('financial_judgments_or_bankruptcy', v)} /></Field>
            {f.financial_judgments_or_bankruptcy && (
              <Field full label="Please provide brief detail"><Textarea rows={2} value={f.financial_details} onChange={e => set('financial_details', e.target.value)} className={inputCls} /></Field>
            )}
            <Field full label="Have you received treatment for substance abuse in the last 5 years?"><YesNo name="substance" value={f.substance_abuse_treatment} onChange={v => set('substance_abuse_treatment', v)} /></Field>
            {f.substance_abuse_treatment && (
              <Field full label="Optional context (not required)"><Textarea rows={2} value={f.substance_abuse_details} onChange={e => set('substance_abuse_details', e.target.value)} className={inputCls} /></Field>
            )}
            <Field full label="Can you pass a pre-employment drug test?"><YesNo name="drug-pass" value={f.can_pass_drug_test} onChange={v => set('can_pass_drug_test', v)} /></Field>
          </Section>

          {/* 9. Education */}
          <Section title="9. Education">
            <Field label="Highest Level Completed">
              <RadioGroup value={f.highest_education} onValueChange={v => set('highest_education', v)} className="flex flex-wrap gap-4 mt-2">
                {['HS/GED', 'Some College', 'Associate', 'Bachelor', 'Master', 'Doctorate'].map(v => (
                  <div key={v} className="flex items-center gap-2"><RadioGroupItem value={v} id={`edu-${v}`} /><Label htmlFor={`edu-${v}`} className="text-sm text-slate-100">{v}</Label></div>
                ))}
              </RadioGroup>
            </Field>
            <Field label="School Name"><Input value={f.school_name} onChange={e => set('school_name', e.target.value)} className={inputCls} /></Field>
            <Field full label="Area of Study / Major"><Input value={f.area_of_study} onChange={e => set('area_of_study', e.target.value)} className={inputCls} /></Field>
          </Section>

          {/* 9b. Additional Education Entries (Iter121A) */}
          <Section title="9b. Additional Education (optional)" subtitle="Add any additional schools, certifications programs, or ongoing coursework.">
            <div className="md:col-span-2 space-y-3" data-testid="apply-edu-history">
              {f.education_history.map((row, i) => (
                <div key={i} className="border border-white/10 rounded-sm p-3 grid grid-cols-1 md:grid-cols-2 gap-3 bg-[#0B192C]">
                  <div className="md:col-span-2 flex items-center justify-between">
                    <p className="text-[10px] font-mono uppercase tracking-wider text-amber-300">Education #{i + 1}</p>
                    <button type="button" onClick={() => removeEduEntry(i)} className="text-[10px] text-red-300 hover:text-red-200 font-mono uppercase" data-testid={`apply-edu-remove-${i}`}>
                      <X size={12} className="inline mr-1" /> Remove
                    </button>
                  </div>
                  <Field label="Institution"><Input value={row.institution} onChange={e => updateEduEntry(i, 'institution', e.target.value)} className={inputCls} /></Field>
                  <Field label="Degree / Program"><Input value={row.degree} onChange={e => updateEduEntry(i, 'degree', e.target.value)} className={inputCls} /></Field>
                  <Field label="Field of Study"><Input value={row.field_of_study} onChange={e => updateEduEntry(i, 'field_of_study', e.target.value)} className={inputCls} /></Field>
                  <Field label="GPA (optional)"><Input value={row.gpa} onChange={e => updateEduEntry(i, 'gpa', e.target.value)} className={inputCls} /></Field>
                  <Field label="Start Date"><Input type="date" value={row.start_date} onChange={e => updateEduEntry(i, 'start_date', e.target.value)} className={inputCls} /></Field>
                  <Field label="End Date"><Input type="date" value={row.end_date} onChange={e => updateEduEntry(i, 'end_date', e.target.value)} className={inputCls} /></Field>
                </div>
              ))}
              <button type="button" onClick={addEduEntry} className="text-xs font-mono uppercase tracking-wider bg-amber-400/90 text-[#0B192C] px-3 py-1.5 rounded-sm hover:bg-amber-300 inline-flex items-center gap-1.5" data-testid="apply-edu-add">
                <Plus size={12} /> Add Education Entry
              </button>
            </div>
          </Section>

          {/* 10. Employment History */}
          <Section title="10. Most Recent Employment">
            <Field label="Total Years Relevant Experience"><Input type="number" min="0" value={f.prior_experience_years} onChange={e => set('prior_experience_years', e.target.value)} className={inputCls} /></Field>
            <Field label="Prior Agency / Employer"><Input value={f.prior_agency} onChange={e => set('prior_agency', e.target.value)} className={inputCls} /></Field>
            <Field label="Role / Title"><Input value={f.prior_role} onChange={e => set('prior_role', e.target.value)} className={inputCls} /></Field>
            <Field label="Start Date"><Input type="date" value={f.prior_employment_start} onChange={e => set('prior_employment_start', e.target.value)} className={inputCls} /></Field>
            <Field label="End Date"><Input type="date" value={f.prior_employment_end} onChange={e => set('prior_employment_end', e.target.value)} className={inputCls} /></Field>
            <Field label="Supervisor Name"><Input value={f.prior_supervisor_name} onChange={e => set('prior_supervisor_name', e.target.value)} className={inputCls} /></Field>
            <Field label="Supervisor Phone"><Input value={f.prior_supervisor_phone} onChange={e => set('prior_supervisor_phone', e.target.value)} className={inputCls} /></Field>
            <Field full label="Reason for Leaving"><Input value={f.prior_reason_for_leaving} onChange={e => set('prior_reason_for_leaving', e.target.value)} className={inputCls} /></Field>
            <Field full label="Relevant Certifications"><Textarea rows={2} value={f.relevant_certifications} onChange={e => set('relevant_certifications', e.target.value)} className={inputCls} /></Field>
          </Section>

          {/* Military */}
          <Section title="10a. Military Experience">
            <div className="md:col-span-2 flex items-center gap-2">
              <Checkbox id="mil" checked={f.has_military_background} onCheckedChange={v => set('has_military_background', !!v)} />
              <Label htmlFor="mil" className="text-sm text-slate-100">I have military service experience</Label>
            </div>
            {f.has_military_background && (
              <>
                <Field label="Branch"><Input value={f.military_branch} onChange={e => set('military_branch', e.target.value)} className={inputCls} /></Field>
                <Field label="Rank at Separation"><Input value={f.military_rank} onChange={e => set('military_rank', e.target.value)} className={inputCls} /></Field>
                <Field label="Years of Service"><Input value={f.military_service_years} onChange={e => set('military_service_years', e.target.value)} className={inputCls} /></Field>
              </>
            )}
          </Section>

          {/* LE */}
          <Section title="10b. Law Enforcement Experience">
            <div className="md:col-span-2 flex items-center gap-2">
              <Checkbox id="le" checked={f.has_law_enforcement_experience} onCheckedChange={v => set('has_law_enforcement_experience', !!v)} />
              <Label htmlFor="le" className="text-sm text-slate-100">I have prior law enforcement experience</Label>
            </div>
            {f.has_law_enforcement_experience && (
              <>
                <Field label="Agency"><Input value={f.le_agency} onChange={e => set('le_agency', e.target.value)} className={inputCls} /></Field>
                <Field label="Role / Rank"><Input value={f.le_role} onChange={e => set('le_role', e.target.value)} className={inputCls} /></Field>
                <Field label="Years of Service"><Input value={f.le_service_years} onChange={e => set('le_service_years', e.target.value)} className={inputCls} /></Field>
              </>
            )}
          </Section>

          {/* 10c. Additional Work History (Iter121A) */}
          <Section title="10c. Additional Work History" subtitle="List prior employers beyond your most-recent job. Most-recent first is preferred.">
            <div className="md:col-span-2 space-y-3" data-testid="apply-work-history">
              {f.work_history.map((row, i) => (
                <div key={i} className="border border-white/10 rounded-sm p-3 grid grid-cols-1 md:grid-cols-2 gap-3 bg-[#0B192C]">
                  <div className="md:col-span-2 flex items-center justify-between">
                    <p className="text-[10px] font-mono uppercase tracking-wider text-amber-300">Employer #{i + 1}</p>
                    <button type="button" onClick={() => removeWorkEntry(i)} className="text-[10px] text-red-300 hover:text-red-200 font-mono uppercase" data-testid={`apply-work-remove-${i}`}>
                      <X size={12} className="inline mr-1" /> Remove
                    </button>
                  </div>
                  <Field label="Employer"><Input value={row.employer} onChange={e => updateWorkEntry(i, 'employer', e.target.value)} className={inputCls} /></Field>
                  <Field label="Role / Title"><Input value={row.role} onChange={e => updateWorkEntry(i, 'role', e.target.value)} className={inputCls} /></Field>
                  <Field label="Start Date"><Input type="date" value={row.start_date} onChange={e => updateWorkEntry(i, 'start_date', e.target.value)} className={inputCls} /></Field>
                  <Field label="End Date"><Input type="date" value={row.end_date} onChange={e => updateWorkEntry(i, 'end_date', e.target.value)} className={inputCls} /></Field>
                  <Field label="Supervisor Name"><Input value={row.supervisor_name} onChange={e => updateWorkEntry(i, 'supervisor_name', e.target.value)} className={inputCls} /></Field>
                  <Field label="Supervisor Phone"><Input value={row.supervisor_phone} onChange={e => updateWorkEntry(i, 'supervisor_phone', e.target.value)} className={inputCls} /></Field>
                  <Field full label="Reason for Leaving"><Input value={row.reason_for_leaving} onChange={e => updateWorkEntry(i, 'reason_for_leaving', e.target.value)} className={inputCls} /></Field>
                  <div className="md:col-span-2 flex items-center gap-2">
                    <Checkbox checked={!!row.may_contact} onCheckedChange={v => updateWorkEntry(i, 'may_contact', !!v)} />
                    <Label className="text-sm text-slate-100">OK for Nexus to contact this employer as part of verification</Label>
                  </div>
                </div>
              ))}
              <button type="button" onClick={addWorkEntry} className="text-xs font-mono uppercase tracking-wider bg-amber-400/90 text-[#0B192C] px-3 py-1.5 rounded-sm hover:bg-amber-300 inline-flex items-center gap-1.5" data-testid="apply-work-add">
                <Plus size={12} /> Add Prior Employer
              </button>
            </div>
          </Section>

          {/* 11. References */}
          <Section title="11. Professional References" subtitle="Three references, not related to you.">
            {[1, 2, 3].map(n => (
              <React.Fragment key={n}>
                <Field label={`Reference ${n} — Name`}><Input value={f[`reference${n}_name`]} onChange={e => set(`reference${n}_name`, e.target.value)} className={inputCls} /></Field>
                <Field label={`Reference ${n} — Relationship`}><Input value={f[`reference${n}_relationship`]} onChange={e => set(`reference${n}_relationship`, e.target.value)} className={inputCls} /></Field>
                <Field full label={`Reference ${n} — Phone`}><Input value={f[`reference${n}_phone`]} onChange={e => set(`reference${n}_phone`, e.target.value)} className={inputCls} /></Field>
              </React.Fragment>
            ))}
          </Section>

          {/* 11b. Certifications (Iter121A) */}
          <Section title="11b. Licenses & Certifications (Tracked)" subtitle="Add any professional certifications with expiration dates so we can track credential currency.">
            <div className="md:col-span-2 space-y-3" data-testid="apply-cert-list">
              {f.certifications_list.map((row, i) => (
                <div key={i} className="border border-white/10 rounded-sm p-3 grid grid-cols-1 md:grid-cols-2 gap-3 bg-[#0B192C]">
                  <div className="md:col-span-2 flex items-center justify-between">
                    <p className="text-[10px] font-mono uppercase tracking-wider text-amber-300">Cert #{i + 1}</p>
                    <button type="button" onClick={() => removeCertEntry(i)} className="text-[10px] text-red-300 hover:text-red-200 font-mono uppercase" data-testid={`apply-cert-remove-${i}`}>
                      <X size={12} className="inline mr-1" /> Remove
                    </button>
                  </div>
                  <Field label="Certification / License"><Input value={row.name} onChange={e => updateCertEntry(i, 'name', e.target.value)} className={inputCls} /></Field>
                  <Field label="Issuing Body"><Input value={row.issuer} onChange={e => updateCertEntry(i, 'issuer', e.target.value)} className={inputCls} placeholder="e.g. TX DPS, State Bar" /></Field>
                  <Field label="License / Cert Number"><Input value={row.license_number} onChange={e => updateCertEntry(i, 'license_number', e.target.value)} className={inputCls} /></Field>
                  <Field label="Issued Date"><Input type="date" value={row.issued_date} onChange={e => updateCertEntry(i, 'issued_date', e.target.value)} className={inputCls} /></Field>
                  <Field full label="Expiration Date"><Input type="date" value={row.expiration_date} onChange={e => updateCertEntry(i, 'expiration_date', e.target.value)} className={inputCls} /></Field>
                </div>
              ))}
              <button type="button" onClick={addCertEntry} className="text-xs font-mono uppercase tracking-wider bg-amber-400/90 text-[#0B192C] px-3 py-1.5 rounded-sm hover:bg-amber-300 inline-flex items-center gap-1.5" data-testid="apply-cert-add">
                <Plus size={12} /> Add Certification
              </button>
            </div>
          </Section>

          {/* 12. Why Nexus */}
          <Section title="12. Additional Information">
            <Field full label="Why are you interested in working with Nexus?"><Textarea rows={3} value={f.why_interested} onChange={e => set('why_interested', e.target.value)} className={inputCls} /></Field>
            <Field full label="Availability Notes (travel, weekends, nights)"><Textarea rows={2} value={f.availability_notes} onChange={e => set('availability_notes', e.target.value)} className={inputCls} /></Field>
          </Section>

          {/* 12a. Structured Availability (Iter121A) */}
          <Section title="12a. Availability & Communication">
            <Field full label="Days Available">
              <MultiCheck name="days" options={['MON','TUE','WED','THU','FRI','SAT','SUN']} value={f.availability_days} onChange={v => set('availability_days', v)} />
            </Field>
            <Field full label="Shifts Available">
              <MultiCheck name="shifts" options={['DAY (7a–3p)','SWING (3p–11p)','GRAVEYARD (11p–7a)','ON-CALL']} value={f.availability_shifts} onChange={v => set('availability_shifts', v)} />
            </Field>
            <Field label="Max Weekly Hours"><Input value={f.max_weekly_hours} onChange={e => set('max_weekly_hours', e.target.value)} className={inputCls} placeholder="e.g. 40, 50, 60+" /></Field>
            <Field label="Willing to Travel (multi-day)"><YesNo name="travel" value={f.willing_to_travel} onChange={v => set('willing_to_travel', v)} /></Field>
            <Field label="Willing to Relocate"><YesNo name="relocate" value={f.willing_to_relocate} onChange={v => set('willing_to_relocate', v)} /></Field>
            <Field full label="Languages Spoken">
              <MultiCheck name="lang-spoken" options={['English','Spanish','Vietnamese','French','Mandarin','Arabic','Other']} value={f.languages_spoken} onChange={v => set('languages_spoken', v)} />
            </Field>
            <Field full label="Languages Written">
              <MultiCheck name="lang-written" options={['English','Spanish','Vietnamese','French','Mandarin','Arabic','Other']} value={f.languages_written} onChange={v => set('languages_written', v)} />
            </Field>
          </Section>

          {/* 13. Background Check Consent */}
          <Section title="13. Background Check Consent" subtitle="Required for all investigator hires.">
            <div className="md:col-span-2 space-y-2">
              <label className="flex items-start gap-2 text-sm text-slate-100"><Checkbox checked={f.consent_background_check} onCheckedChange={v => set('consent_background_check', !!v)} data-testid="consent-bg" /><span>I authorize Nexus Investigation Agency to conduct a full criminal background check.</span></label>
              <label className="flex items-start gap-2 text-sm text-slate-100"><Checkbox checked={f.consent_driving_record} onCheckedChange={v => set('consent_driving_record', !!v)} data-testid="consent-driving" /><span>I authorize verification of my driving record.</span></label>
              <label className="flex items-start gap-2 text-sm text-slate-100"><Checkbox checked={f.consent_reference_verification} onCheckedChange={v => set('consent_reference_verification', !!v)} data-testid="consent-ref" /><span>I authorize contact with my references and prior employers.</span></label>
              <label className="flex items-start gap-2 text-sm text-slate-100"><Checkbox checked={f.consent_tx_dps_verification} onCheckedChange={v => set('consent_tx_dps_verification', !!v)} data-testid="consent-dps" /><span>I authorize verification with TX DPS / licensing boards as applicable.</span></label>
              <label className="flex items-start gap-2 text-sm text-slate-100"><Checkbox checked={f.consent_drug_test} onCheckedChange={v => set('consent_drug_test', !!v)} data-testid="consent-drug" /><span>I consent to pre-employment and random drug testing.</span></label>
            </div>
            <Field full label="Type your initials to confirm consent"><Input maxLength={4} value={f.consent_initials_background} onChange={e => set('consent_initials_background', e.target.value.toUpperCase())} className={inputCls} placeholder="e.g. JPS" /></Field>
          </Section>

          {/* 14. Confidentiality */}
          <Section title="14. Confidentiality Agreement">
            <div className="md:col-span-2">
              <label className="flex items-start gap-2 text-sm text-slate-100"><Checkbox checked={f.consent_confidentiality} onCheckedChange={v => set('consent_confidentiality', !!v)} data-testid="consent-conf" /><span>I understand that all case, client, subject and agency information is strictly confidential, and I agree not to disclose any such information during or after my employment with Nexus.</span></label>
            </div>
            <Field full label="Type your initials to confirm"><Input maxLength={4} value={f.consent_initials_confidentiality} onChange={e => set('consent_initials_confidentiality', e.target.value.toUpperCase())} className={inputCls} placeholder="e.g. JPS" /></Field>
          </Section>

          {/* 15. Certification & Signature */}
          <Section title="15. Certification & Signature">
            <div className="md:col-span-2">
              <label className="flex items-start gap-2 text-sm text-slate-100"><Checkbox checked={f.certified_true} onCheckedChange={v => set('certified_true', !!v)} data-testid="cert-true" /><span>I certify that the information provided on this application is true, accurate and complete to the best of my knowledge. I understand that any false statement, omission or misrepresentation is grounds for rejection or, if hired, immediate termination.</span></label>
            </div>
            <Field label="Typed Signature (Full Legal Name) *"><Input value={f.applicant_signature} onChange={e => set('applicant_signature', e.target.value)} className={inputCls} data-testid="apply-signature" required /></Field>
            <Field label="Date *"><Input type="date" value={f.signature_date} onChange={e => set('signature_date', e.target.value)} className={inputCls} required /></Field>
          </Section>

          <div className="flex items-center justify-between gap-3 pt-2">
            <Link to="/login" className="text-xs text-slate-300 hover:text-white font-mono uppercase tracking-wider" data-testid="apply-cancel">Cancel</Link>
            <Button type="submit" disabled={submitting} className="bg-amber-400 hover:bg-amber-300 text-[#0B192C] font-heading font-semibold rounded-sm px-8" data-testid="apply-submit-btn">
              {submitting ? 'Submitting…' : 'Submit Application'}
            </Button>
          </div>
        </form>

        <p className="text-[10px] text-slate-400 font-mono uppercase tracking-wider text-center mt-6">
          By applying you authorise Nexus Investigation Agency to verify all provided information and contact references.
        </p>
      </div>
    </div>
  );
};

export default Apply;
